import { Grid, Typography, Box, ClickAwayListener, MenuItem } from '@mui/material';
import { TextFieldv2 } from '../TextFieldv2';
import { IObjectRaw } from 'views/backoffice/CustomObjects/types';
import { InputMaskPhone } from 'ui-component/RecordView/components/InputMaskPhoneCell';
import { StandaloneFormInlineInput } from '../Sections';
import { useObjectFromMetadata } from 'views/backoffice/BusinessEntities/components/FormSection/hooks';
import { contactsObjectToFieldMapping } from 'views/backoffice/BusinessEntities/components/FormSection/utils';
import { PanelSectionContainer, PanelSection } from 'ui-component/accordion';
import { ConditionalWrapper } from 'ui-component/ConditionalWrapper';

export type ObjectSideInputProps = {
    title: string;
    inputDefinition: Pick<IObjectRaw, 'id' | 'name'>;
    onClose: () => void;
    value: Record<string, any>;
    onChange: (value: Record<string, any>) => void;
    onChangeSelectedField: (field: string) => void;
};

export const ObjectSideInput = ({ title, inputDefinition, onClose, value, onChange, onChangeSelectedField }: ObjectSideInputProps) => {
    const { objectPropertiesById } = useObjectFromMetadata(title);

    const fields = Object.values(objectPropertiesById)
        .sort((a, b) => a.order - b.order)
        .filter((el) => el.enabled);

    const handleChangeProperty = (id: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange({ ...value, [id]: e.target.value });
    };

    const isContactsObject = Object.keys(contactsObjectToFieldMapping).includes(title);

    return (
        <ClickAwayListener onClickAway={onClose}>
            <Box
                component={Grid}
                container
                flexDirection="column"
                sx={(theme) => ({
                    width: '100%',
                    height: '100%',
                    px: '6px',
                    py: '12px',
                    border: 'solid 1px #EDEEFE',
                    borderRadius: '8px',
                    '& > .MuiGrid-item': { width: '100%' }
                })}
            >
                <Grid item container xs="auto" alignItems="center" sx={{ mb: '15px', width: '100%' }}>
                    <Grid item xs>
                        <Typography fontSize="16px" fontWeight={700} color="secondary">
                            {title}
                        </Typography>
                    </Grid>
                </Grid>

                {isContactsObject && (
                    <PanelSectionContainer>
                        <PanelSection title="Field Settings">
                            <Grid item container xs="auto" sx={{ mb: '6px', paddingInline: '12px' }}>
                                <Grid item xs={12}>
                                    <StandaloneFormInlineInput
                                        sx={{ mb: '12px' }}
                                        labelGridItemProps={{ xs: 6 }}
                                        label="Field name"
                                        formComponent={
                                            <TextFieldv2
                                                select
                                                value={title}
                                                onChange={(e) => onChangeSelectedField(e.target.value)}
                                                SelectProps={{ MenuProps: { disablePortal: true } }}
                                            >
                                                {Object.keys(contactsObjectToFieldMapping).map((option) => (
                                                    <MenuItem key={option} value={option}>
                                                        {option}
                                                    </MenuItem>
                                                ))}
                                            </TextFieldv2>
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </PanelSection>
                    </PanelSectionContainer>
                )}
                <ConditionalWrapper
                    condition={isContactsObject}
                    wrapper={(children) => (
                        <PanelSectionContainer>
                            <PanelSection title="Contact Information">{children}</PanelSection>
                        </PanelSectionContainer>
                    )}
                >
                    <Box sx={{ paddingInline: '12px' }}>
                        {fields.map(({ id, name, isRequired, dataType }, idx) => (
                            <Grid key={id} item container xs="auto" sx={{ mb: '6px' }}>
                                <Grid item xs={12}>
                                    <StandaloneFormInlineInput
                                        sx={{ mb: '12px' }}
                                        labelGridItemProps={{ xs: 6 }}
                                        label={name}
                                        formComponent={
                                            <>
                                                {dataType === 'phone' || name.toLowerCase().includes('phone') ? (
                                                    <InputMaskPhone
                                                        TextFieldComponent={TextFieldv2}
                                                        autoFocus={idx === 0}
                                                        value={value[id] || ''}
                                                        onChange={handleChangeProperty(+id)}
                                                        countryCodeProps={{
                                                            sx: {
                                                                height: '100%',
                                                                maxHeight: '37px',
                                                                borderRadius: '8px',
                                                                mr: '4px'
                                                            },
                                                            selectedOptionSx: { color: '#212121' }
                                                        }}
                                                    />
                                                ) : (
                                                    <TextFieldv2
                                                        autoFocus={idx === 0}
                                                        value={value[id]}
                                                        onChange={handleChangeProperty(+id)}
                                                    />
                                                )}
                                            </>
                                        }
                                    />
                                </Grid>
                            </Grid>
                        ))}
                    </Box>
                </ConditionalWrapper>
            </Box>
        </ClickAwayListener>
    );
};
